<template>
  <div class="teacher-detail-page">
    <!-- 顶部背景 -->
    <div class="header-bg">
      <div class="header-content">
        <div class="back-btn" @click="goBack">
          <van-icon name="arrow-left" />
        </div>
        <div class="teacher-info">
          <div class="teacher-name">{{ teacherName }}
            <div class="date-range">[{{ orgName }}]</div>
          </div>
          <div class="date-range" v-if="dateRangeDisplay">{{ dateRangeDisplay }}</div>
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-card">
      <div class="stats-header">
        <div class="stats-title">
          <span class="title-icon teacher-icon"></span>
          老师课时统计
        </div>
      </div>
      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-value">{{ overviewData.lessonCount }}</div>
          <div class="stat-label">上课次数</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ overviewData.attendanceCount }}</div>
          <div class="stat-label">到课人次</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ overviewData.teacherHours }}</div>
          <div class="stat-label">老师课时</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ overviewData.studentHours }}</div>
          <div class="stat-label">学生课时</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ overviewData.classCount }}</div>
          <div class="stat-label">班级数量</div>
        </div>
      </div>
    </div>

    <!-- 类型统计堆叠图 -->
    <div class="chart-section">
      <div class="section-title">
        <span class="title-icon type"></span>
        类型统计
      </div>
      <div class="dimension-toggle">
        <div
          v-for="option in dimensionOptions"
          :key="option.value"
          class="toggle-btn"
          :class="{ active: selectedDimension === option.value }"
          @click="handleDimensionChange(option.value)"
        >
          {{ option.label }}
        </div>
      </div>
      <div ref="typeChart" class="bar-chart"></div>
    </div>

    <!-- 班级课时列表 -->
    <div class="list-section">
      <div class="section-title-row">
        <div class="section-title">
          <span class="title-icon class-icon"></span>
          班级课时列表
        </div>
        <span class="list-count">共 {{ classList.length }} 条</span>
      </div>
      <div class="list-content list-content--class-hours">
        <template v-if="classList.length > 0">
          <div class="list-item header list-item--class-hours">
            <span class="col-class-name">校区</span>
            <span class="col-class-name">班级</span>
            <span class="col-class-code">班级编号</span>
            <span class="col-hours-compact">老师课时</span>
            <span class="col-hours-compact">学生课时</span>
            <span v-if="userType !== '22'" class="col-hours-compact">学费消耗</span>
            <span class="col-hours-compact">出勤人次</span>
            <span class="col-hours-compact">应到人次</span>
            <span class="col-hours-compact">出勤率</span>
          </div>
          <div
            v-for="(item, index) in classList"
            :key="index"
            class="list-item list-item--class-hours"
            :class="{ odd: index % 2 === 1 }"
          >
            <span class="col-class-name">{{ item.orgName }}</span>
            <span class="col-class-name">{{ item.className }}</span>
            <span class="col-class-code">{{ item.classCode }}</span>
            <span class="col-hours-compact highlight">{{ item.teacherHours || 0 }}</span>
            <span class="col-hours-compact highlight">{{ item.studentHours || 0 }}</span>
            <span v-if="userType !== '22'" class="col-hours-compact">{{ item.studentAmount || 0 }}</span>
            <span class="col-hours-compact">{{ item.attendanceCount || 0 }}</span>
            <span class="col-hours-compact">{{ item.expectedAttendanceCount || 0 }}</span>
            <span class="col-hours-compact">{{ formatAttendanceRate(item.attendanceRate) }}</span>
          </div>
        </template>
        <EmptyData v-else tips="暂无班级课时数据" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as echarts from 'echarts'
import dayjs from 'dayjs'
import { QUICK_KEY, QUICK_TEXT, getDateRangeByType, formatString } from '@/components/dateFilterPanel/utils.js'
import EmptyData from '@/components/EmptyData.vue'
import { postTeacherCourse } from '@/api/report'

const route = useRoute()
const router = useRouter()

const PC_CHART_MIN_WIDTH = 768
const isPC = ref(typeof window !== 'undefined' && window.innerWidth >= PC_CHART_MIN_WIDTH)
const PC_BAR_WIDTH = 40
const MOBILE_BAR_WIDTH_PERCENT = '35%'
const CHART_BAR_MAX_WIDTH = 80
const CHART_X_AXIS_LABEL_ROTATE = 25
const CHART_X_AXIS_ROTATE_WHEN_COUNT_GT = 4

const teacherId = ref(route.query.teacherId || '')
const teacherName = ref(route.query.teacherName || '')
const orgId = ref(route.query.orgId || '')
const orgName = ref(route.query.orgName || '')
const startDate = ref(route.query.startDate || '')
const endDate = ref(route.query.endDate || '')

const dateRangeDisplay = computed(() => {
  if (startDate.value && endDate.value) {
    return `${startDate.value} 至 ${endDate.value}`
  }
  return ''
})

const overviewData = ref({
  teacherHours: 0,
  studentHours: 0,
  lessonCount: 0,
  classCount: 0,
  attendanceCount: 0
})

const typeData = ref([])
const rawTypeStats = []
const isAllOrg = ref(false)

const selectedDimension = ref('studentHours')

const userType = ref('')

const dimensionOptions = computed(() => {
  const options = [
    { label: '上课次数', value: 'lesson' },
    { label: '到课人次', value: 'attendance' },
    { label: '老师课时', value: 'teacherHours' },
    { label: '学生课时', value: 'studentHours' }
  ]
  if (userType.value !== '22') {
    options.push({ label: '学费消耗', value: 'studentAmount' })
  }
  return options
})

const getClassField = (dim) => {
  const map = {
    attendance: 'classCourseAttendanceCount',
    lesson: 'classCourseLessonCount',
    teacherHours: 'classCourseTeacherHours',
    studentHours: 'classCourseStudentHours',
    studentAmount: 'classCourseStudentAmount'
  }
  return map[dim] || 'classCourseStudentHours'
}

const getOneToOneField = (dim) => {
  const map = {
    attendance: 'oneToOneAttendanceCount',
    lesson: 'oneToOneLessonCount',
    teacherHours: 'oneToOneTeacherHours',
    studentHours: 'oneToOneStudentHours',
    studentAmount: 'oneToOneStudentAmount'
  }
  return map[dim] || 'oneToOneStudentHours'
}

const updateTypeChartData = () => {
  if (rawTypeStats.length === 0) return

  const classField = getClassField(selectedDimension.value)
  const oneToOneField = getOneToOneField(selectedDimension.value)

  if (isAllOrg.value) {
    // 全校区：X轴为校区名字，每组显示班课和1V1
    typeData.value = rawTypeStats.map(item => ({
      periodName: item.orgName || '未知机构',
      classHours: item[classField] || 0,
      oneToOneHours: item[oneToOneField] || 0
    }))
  } else {
    // 单个校区：X轴为班课和1V1两项
    const singleItem = rawTypeStats[0] || {}
    typeData.value = [
      {
        periodName: '班课',
        classHours: singleItem[classField] || 0,
        oneToOneHours: 0
      },
      {
        periodName: '1V1',
        classHours: 0,
        oneToOneHours: singleItem[oneToOneField] || 0
      }
    ]
  }
}

const handleDimensionChange = (dim) => {
  selectedDimension.value = dim
  updateTypeChartData()
  renderTypeChart()
}

const classList = ref([])

const typeChart = ref(null)
let typeChartInstance = null
let chartResizeObserver = null

const typeLegendSelected = ref({ 班课: true, '1V1': true })

const getTypeVisibleTotal = (row, sel) => {
  const c = Number(row.classHours) || 0
  const o = Number(row.oneToOneHours) || 0
  let sum = 0
  if (sel['班课'] !== false) sum += c
  if (sel['1V1'] !== false) sum += o
  return sum
}

const getTypeTopSegment = (row, sel) => {
  const c = Number(row.classHours) || 0
  const o = Number(row.oneToOneHours) || 0
  const showC = sel['班课'] !== false && c > 0
  const showO = sel['1V1'] !== false && o > 0
  if (showO) return '1V1'
  if (showC) return '班课'
  return null
}

const typeAllOrgTotalLabelFormatter = (isOneToOneSeries) => (params) => {
  const row = typeData.value[params.dataIndex]
  if (!row) return ''
  const sel = typeLegendSelected.value
  const sum = getTypeVisibleTotal(row, sel)
  if (!sum) return ''
  const top = getTypeTopSegment(row, sel)
  if (isOneToOneSeries) return top === '1V1' ? String(sum) : ''
  return top === '班课' ? String(sum) : ''
}

const onTypeLegendSelectChanged = (e) => {
  if (!typeChartInstance) return
  typeLegendSelected.value = { ...typeLegendSelected.value, ...(e.selected || {}) }
  typeChartInstance.setOption(
    {
      series: [
        { label: { formatter: typeAllOrgTotalLabelFormatter(false) } },
        { label: { formatter: typeAllOrgTotalLabelFormatter(true) } }
      ]
    },
    false
  )
}

const getBarWidth = () => isPC.value ? PC_BAR_WIDTH : MOBILE_BAR_WIDTH_PERCENT

const chartTooltipTextStyle = { color: '#333', fontSize: 12, align: 'left' }
const chartTooltipExtraCssText = 'text-align:left;'

const axisTooltipFormatterLeft = (params) => {
  if (!Array.isArray(params) || !params.length) return ''
  const title = params[0].axisValueLabel ?? params[0].name ?? ''
  // 全校区时X轴是校区名，只显示班课；单个校区时显示所有
  const isSingleOrg = rawTypeStats.length === 1
  const filteredParams = isSingleOrg ? params : params.filter(p => p.seriesName === '班课')
  const rows = filteredParams.map(
    (p) =>
      `<div style="text-align:left;line-height:22px">${p.marker}<span style="margin:0 6px 0 4px">${p.seriesName}</span><span>${p.value}</span></div>`
  )
  return `<div style="text-align:left"><div style="font-weight:600;margin-bottom:6px">${title}</div>${rows.join('')}</div>`
}

const renderTypeChart = () => {
  if (!typeChart.value) return

  if (!typeChartInstance) {
    typeChartInstance = echarts.init(typeChart.value)
    typeChartInstance.on('legendselectchanged', onTypeLegendSelectChanged)
  }

  const typeXCount = typeData.value.length
  const typeXRotate = typeXCount > CHART_X_AXIS_ROTATE_WHEN_COUNT_GT ? CHART_X_AXIS_LABEL_ROTATE : 0
  const typeGridBottom = typeXRotate ? '16%' : '14%'

  // 单个校区时1V1也要展示
  const showOneToOne = isAllOrg.value || rawTypeStats.length === 1

  const option = {
    tooltip: {
      trigger: 'axis',
      renderMode: 'html',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e8e8e8',
      borderWidth: 1,
      textStyle: chartTooltipTextStyle,
      extraCssText: chartTooltipExtraCssText,
      formatter: axisTooltipFormatterLeft
    },
    legend: {
      data: showOneToOne ? ['班课', '1V1'] : ['班课'],
      selected: { ...typeLegendSelected.value },
      bottom: 5,
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { fontSize: 12, color: '#666' },
      itemGap: 20
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: typeGridBottom,
      top: '8%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: typeData.value.map(item => item.periodName),
      axisLabel: {
        fontSize: 12,
        color: '#666',
        rotate: typeXRotate,
        interval: 0,
        align: typeXRotate ? 'right' : 'center'
      },
      axisLine: { lineStyle: { color: '#e8e8e8' } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      name: '',
      nameTextStyle: { color: '#999', fontSize: 11 },
      axisLabel: { fontSize: 11, color: '#999' },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: '班课',
        type: 'bar',
        stack: 'total',
        barWidth: getBarWidth(),
        barMaxWidth: CHART_BAR_MAX_WIDTH,
        data: typeData.value.map(item => item.classHours),
        itemStyle: { color: '#3478f6', borderRadius: [0, 0, 4, 4] },
        label: {
          show: true,
          position: 'top',
          formatter: typeAllOrgTotalLabelFormatter(false),
          fontSize: 14,
          fontWeight: 600,
          color: '#333'
        }
      },
      ...(showOneToOne ? [{
        name: '1V1',
        type: 'bar',
        stack: 'total',
        barWidth: getBarWidth(),
        barMaxWidth: CHART_BAR_MAX_WIDTH,
        data: typeData.value.map(item => item.oneToOneHours),
        itemStyle: { color: '#ff9f43', borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          formatter: typeAllOrgTotalLabelFormatter(true),
          fontSize: 14,
          fontWeight: 600,
          color: '#333'
        }
      }] : [])
    ]
  }

  typeChartInstance.setOption(option, { notMerge: true })
}

const flushChartResize = () => {
  nextTick(() => {
    typeChartInstance?.resize()
  })
}

const handleResize = () => {
  isPC.value = typeof window !== 'undefined' && window.innerWidth >= PC_CHART_MIN_WIDTH
  renderTypeChart()
  flushChartResize()
}

const formatAttendanceRate = (rate) => {
  if (rate === null || rate === undefined || rate === '') return '0%'
  const num = Number(rate)
  if (isNaN(num)) return '0%'
  return `${(num)}%`
}

const goBack = () => {
  router.back()
}

const loadData = async () => {
  if (!startDate.value || !endDate.value) return

  try {
    const res = await postTeacherCourse({
      orgId: orgId.value,
      teacherId: teacherId.value,
      startDate: startDate.value,
      endDate: endDate.value
    })
    console.log('老师详情数据返回:', res)

    if (res.code === '00' && res.data) {
      const data = res.data
      const statisticsList = data.statisticsList || []
      const typeStatistics = data.typeStatistics || []

      // 判断是否为全校区（多个机构）
      isAllOrg.value = typeStatistics.length > 1

      // 计算总览数据
      const teacherHoursTotal = statisticsList.reduce((sum, item) => sum + (Number(item.teacherHours) || 0), 0)
      const studentHoursTotal = statisticsList.reduce((sum, item) => sum + (Number(item.studentHours) || 0), 0)
      const lessonCountTotal = statisticsList.reduce((sum, item) => sum + (Number(item.lessonCount) || 0), 0)
      const attendanceCountTotal = statisticsList.reduce((sum, item) => sum + (Number(item.attendanceCount) || 0), 0)

      overviewData.value = {
        teacherHours: teacherHoursTotal,
        studentHours: studentHoursTotal,
        lessonCount: lessonCountTotal,
        classCount: statisticsList.length,
        attendanceCount: attendanceCountTotal
      }

      // 班级课时列表
      classList.value = statisticsList

      // 类型统计图数据
      rawTypeStats.length = 0
      rawTypeStats.push(...typeStatistics)

      updateTypeChartData()

      await nextTick()
      renderTypeChart()
    } else {
      console.warn('老师详情接口返回数据格式异常:', res)
      classList.value = []
    }
  } catch (error) {
    console.error('加载老师详情数据失败:', error)
    classList.value = []
  }
}

const getPeriodName = (period) => {
  const names = { '1': '小学', '2': '初中', '3': '高中' }
  return names[period] || '未知学段'
}

onMounted(() => {
  userType.value = localStorage.getItem('userType') || ''
  // 如果是22类型用户，且当前选中的是学费消耗，切换到学生课时
  if (userType.value === '22' && selectedDimension.value === 'studentAmount') {
    selectedDimension.value = 'studentHours'
  }
  loadData()
  window.addEventListener('resize', handleResize)
  nextTick(() => {
    chartResizeObserver = new ResizeObserver(() => flushChartResize())
    if (typeChart.value) chartResizeObserver.observe(typeChart.value)
  })
})

onBeforeUnmount(() => {
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  chartResizeObserver?.disconnect()
  chartResizeObserver = null
  typeChartInstance?.dispose()
})
</script>

<style lang="scss" scoped>
.teacher-detail-page {
  background: linear-gradient(180deg, #e8f4ff 0%, #f5f6fa 30%);
  min-height: 100vh;
  padding-bottom: 20px;
}

.header-bg {
  background: linear-gradient(135deg, #3478f6 0%, #5a9fff 100%);
  padding: 8px 16px 40px;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 16px rgba(52, 120, 246, 0.2);
  width: 100%;
  box-sizing: border-box;
  margin-left: 0;
  margin-right: 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}

.teacher-info {
  flex: 1;
  margin-right: 36px;
  .teacher-name {
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    justify-content: center;
  }
  .date-range {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
  }
}

.stats-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(52, 120, 246, 0.08);
  margin: -30px 12px 16px 12px;
  padding: 16px;
  position: relative;
  z-index: 10;
}

.stats-header {
  margin-bottom: 20px;
}

.stats-title {
  font-size: 17px;
  font-weight: 600;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 8px;

  .title-icon {
    width: 4px;
    height: 16px;
    background: linear-gradient(180deg, #13c2c2 0%, #36cfc9 100%);
    border-radius: 2px;
  }
}

.stats-grid {
  display: flex;
  gap: 6px;
  flex-wrap: nowrap;
  width: 100%;
  overflow-x: scroll;
}

.stat-item {
  flex: 1;
  background: linear-gradient(135deg, #f8faff 0%, #fff 100%);
  border-radius: 10px;
  padding: 10px 8px;
  text-align: center;
  border: 1px solid #e8eef5;
  min-width: 52px;
  transition: all 0.3s ease;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  white-space: nowrap;
  line-height: 1.2;
  background: linear-gradient(135deg, #3478f6 0%, #5a9fff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-label {
  font-size: 11px;
    color: #8c8c8c;
    margin: 0;
    white-space: nowrap;
    line-height: 1.2;
}

.chart-section {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(52, 120, 246, 0.08);
  margin: 0 12px 16px 12px;
  padding: 12px;
}

.section-title {
  font-size: 17px;
  font-weight: 600;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 8px;

  .title-icon {
    width: 4px;
    height: 16px;
    background: linear-gradient(180deg, #ff9f43 0%, #ffbe76 100%);
    border-radius: 2px;

    &.type {
      background: linear-gradient(180deg, #ff9f43 0%, #ffbe76 100%);
    }

    &.class-icon {
      background: linear-gradient(180deg, #722ed1 0%, #b37feb 100%);
    }
  }
}

.dimension-toggle {
  display: flex;
  gap: 8px;
  margin: 12px 0;
}

.toggle-btn {
  flex: 1;
  padding: 4px 0;
  text-align: center;
  font-size: 12px;
  color: #999;
  background: #f7f8fa;
  border-radius: 8px;
  border: 1px solid transparent;
  transition: all 0.25s ease;
  max-width: 80px;

  &.active {
    color: #4b85ef;
    background: #e8f4ff;
    border-color: #b3d4ff;
    font-weight: 500;
  }

  &:active {
    transform: scale(0.98);
  }
}

.bar-chart {
  width: 100%;
  height: 240px;
}

.list-section {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(52, 120, 246, 0.08);
  margin: 0 12px 16px 12px;
  padding: 12px;
}

.section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.list-count {
  font-size: 13px;
  color: #8c8c8c;
}

.list-content {
  margin-top: 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.list-content--class-hours {
  .list-item--class-hours {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
  }

  .col-class-name,
  .col-class-code,
  .col-hours-compact {
    flex: 1 1 0;
    min-width: 0;
    padding: 8px 4px;
    color: #333;
    text-align: center;
    word-break: break-word;
  }

  .col-class-name {
    text-align: left;
    flex: 1.2 1 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-width: 80px;
  }

  .col-class-code {
    text-align: left;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-width: 80px;
  }

  .col-hours-compact {
    flex-shrink: 0;
    white-space: nowrap;
    min-width: 50px;

    &.highlight {
      color: #3478f6;
      font-weight: 600;
    }
  }
}

.list-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 8px;

  &.header {
    background: #f5f7fa;
    color: #666;
    font-weight: 600;
    font-size: 13px;
    padding: 12px;
    margin-bottom: 0;
    min-width: 530px;
  }

  &.odd {
    background: #fafafa;
  }
}
</style>